/**
 * Main UI logic for sliding drawer to launch jobs.
 */
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Icon, IconInfo} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {useNodeValue} from '@wandb/weave/react';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import YAML from '../../../../services/yaml';
import {
  RunFilesAndFolderWithDirectUrLsQueryResult,
  RunQueueAccessType,
  RunQueuePrioritizationMode,
  TemplateVariable,
  useCreateDefaultResourceConfigMutation,
  useCreateRunQueueMutation,
  usePushToRunQueueMutation,
  useRunFilesAndFolderWithDirectUrLsQuery,
  useUpsertModelMutation,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {jsonParseSafe} from '../../../util/json';
import {safeLocalStorage} from '../../../util/localStorage';
import {queue as queueUrl} from '../../../util/urls';
import {useRunQueuePopover} from '../../RunQueueItemsPopover';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {CodeEditor} from '../CodeEditor';
import {
  DEFAULT_LAUNCH_PROJECT_NAME,
  PRIORITY_LEVEL,
  PRIORITY_LEVEL_METADATA,
} from '../constants';
import {
  isSingleVarString,
  sortTemplateVariablesByConfig,
} from '../DefaultResourceConfig/defaultResourceConfigUtil';
import ResourceVariableForm from '../DefaultResourceConfig/ResourceVariableForm';
import {
  useDestinationProjectOptions,
  usePageProjectAndEntityTarget,
  usePublicQueueAccess,
  useRunQueueItem,
} from '../hooks';
import {
  getJobDefinitionFileOp,
  getJobMetadataOp,
  JobFile,
} from '../JobDetail/utilsJobFile';
import {JobDetails, JobRun, JobVersions} from '../JobDetail/utilTypes';
import {
  filterQueuesBasedOnJobSelection,
  isQueuePrioritizationEnabled,
} from '../Queues/queueUtil';
import {SelectPriority} from '../SelectPriority';
import {ProjectOption, SelectProject} from '../SelectProject2';
import {GroupedQueueOptions, QueueOption, SelectQueue} from '../SelectQueue2';
import {
  isAllowedWandbPublicJob,
  WANDB_ENTITY_NAME,
  WANDB_PUBLIC_QUEUE_NAME,
} from '../utils';
import {Json, JsonObject, LaunchSpec} from '../utilTypes';
import {JobDrawerAdditionalServiceConfigEditor} from './JobDrawerAdditionalServiceConfigEditor';
import * as S from './JobDrawerContentInner.styles';
import {JobDrawerOverrides} from './JobDrawerOverrides';
import {JobDrawerStructuredOverrides} from './JobDrawerStructuredOverrides';
import {
  getDefaultVariableValuesFromQueueVariables,
  getMissingRequiredServices,
  getRelevantVariables,
  validateRequiredServices,
} from './utils';

const ALLOWED_JOBS = ['Evaluate model checkpoint', 'Evaluate API-hosted model'];

type JobDrawerContentInnerProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  job: JobDetails;
  runToClone?: string;
  lockedRunToClone?: boolean;
  customRunColors: Record<string, string>;
  queues: GroupedQueueOptions[];
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  onClose: () => void;
  openConfigImporter: () => void;
  openSecretsView: () => void;
  onTargetEntityChange?: (entityName: string) => void;
  configSourceRun: JobRun | null;
}>;

type AdditionalServiceData = {
  name: string;
  config: JsonObject;
  configString: string;
  displayType: string;
  isValid: boolean;
};

export const getDefaultCloneFrom = (
  runs: JobRun[],
  username: string,
  runToClone?: string
): JobRun | null => {
  if (runToClone) {
    for (const run of runs) {
      if (run.name === runToClone) {
        return run;
      }
    }
  }
  // Runs are sorted creation date descending, so the first run is the most recent.
  // TODO: Some debate over whether this should take job success into account.
  for (const run of runs) {
    if (run.creator?.username === username) {
      return run;
    }
  }
  if (runs.length > 0) {
    return runs[0];
  }
  return null;
};

const findMatchingQueue = ({
  queueName,
  queueEntity,
  queueOptions,
}: {
  queueName: string;
  queueEntity: string;
  queueOptions: GroupedQueueOptions[];
}) => {
  const entityQueue = queueOptions.find(q => q.label === queueEntity);
  if (entityQueue == null) {
    return null;
  }
  const queue = entityQueue.options.find(q => q.queue === queueName);
  if (queue != null && queue.isActive && !queue.disabled) {
    return queue;
  }
  return null;
};

const getDefaultQueue = (
  queues: GroupedQueueOptions[],
  runQueueName?: string | null,
  entityQueueName?: string | null,
  job?: JobDetails
): QueueOption | null => {
  if (queues.length === 0) {
    return null;
  }

  if (
    job &&
    isAllowedWandbPublicJob(job.name, job.projectName, job.entityName)
  ) {
    return findMatchingQueue({
      queueName: WANDB_PUBLIC_QUEUE_NAME,
      queueEntity: WANDB_ENTITY_NAME,
      queueOptions: queues,
    });
  }

  if (entityQueueName != null && runQueueName != null) {
    const queue = findMatchingQueue({
      queueName: runQueueName,
      queueEntity: entityQueueName,
      queueOptions: queues,
    });
    if (queue != null) {
      return queue;
    }
  }

  const group = queues[0];
  if (group.options.length === 0) {
    return null;
  }
  const option = group.options[0];
  if (option.disabled || !option.isActive) {
    return null;
  }
  return option;
};

export const getVersionDescription = (
  versions: JobVersions,
  runTarget: string
) => {
  for (const version of versions) {
    if (version.aliases.some(alias => alias.alias === runTarget)) {
      return version.description;
    }
  }
  return '';
};

const determineQueueInfoFromRunConfig = (run: JobRun | null) => {
  if (!run) {
    return {runsQueueName: null, runsEntityName: null, runQueueItemID: null};
  }
  // TODO: fix up the types for JsonObject to make this less hacky
  const wandbConfig: {[property: string]: Json} = (
    run.config._wandb as {[property: string]: Json}
  )?.value as {[property: string]: Json};
  const runsQueueName = wandbConfig?.launch_queue_name as string;
  const runQueueItemID = wandbConfig?.launch_trace_id as string;
  return {runsQueueName, runsEntityName: run.entityName, runQueueItemID};
};

const pruneTemplateVariableValues = (
  variableValues: Record<string, number | string>,
  queueVariables: TemplateVariable[]
) => {
  const allowed = queueVariables.map(value => {
    return value.name;
  });
  const prunedValues = Object.keys(variableValues)
    .filter(key => allowed.includes(key))
    .reduce((obj: Record<string, number | string>, key) => {
      obj[key] = variableValues[key];
      return obj;
    }, {});
  return prunedValues;
};

const populateJobFilesMap = async (
  queryRunFiles: RunFilesAndFolderWithDirectUrLsQueryResult
): Promise<{[x: string]: JsonObject}> => {
  const files = queryRunFiles.data?.project?.run?.files?.edges ?? [];
  const fetchPromises = files.map(async edge => {
    if (!edge.node || !edge.node.name || !edge.node.directUrl) {
      console.error('Invalid file edge:', edge);
      return null;
    }

    try {
      const response = await fetch(edge.node.directUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      // Get response as text to handle JSON or YAML
      let parsedData;
      try {
        // Responses can only be read once, so clone the response before reading in case
        // we need to read it again to parse as yaml
        parsedData = await response.clone().json();
      } catch {
        try {
          const content = await response.text();
          parsedData = YAML.parse(content);
        } catch {
          console.error(`Failed to parse file ${edge.node.name}`);
          return null;
        }
      }

      return {name: edge.node.name, data: parsedData};
    } catch (error) {
      console.error(`Failed to fetch or parse file ${edge.node.name}:`, error);
      return null;
    }
  });

  const results = await Promise.allSettled(fetchPromises);
  const newFilesMap: {[x: string]: any} = {};
  results.forEach(r => {
    if (r.status === 'fulfilled' && r.value) {
      const name = r.value.name.substring(r.value.name.indexOf('/') + 1);
      newFilesMap[name] = r.value.data;
    } else {
      console.error(`Failed to fetch file`);
    }
  });
  return newFilesMap;
};

enum OverridesMode {
  BASIC = 'basic',
  ADVANCED = 'advanced',
}

export const JobDrawerContentInner = ({
  entityName,
  projectName,
  job,
  runToClone,
  lockedRunToClone,
  customRunColors,
  queues,
  finalizeCTA,
  children,
  overrideHintText,
  onFinalize,
  onClose,
  openConfigImporter,
  openSecretsView,
  onTargetEntityChange,
  configSourceRun,
}: JobDrawerContentInnerProps) => {
  const viewer = useViewer();
  const viewerEntity = viewer?.username ?? '';
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine which runs to show the user as clone options.
  const previousRuns = useMemo(
    () => job.runs.filter(run => run.version === job.versionInfo.runTargetNum),
    [job.runs, job.versionInfo.runTargetNum]
  );
  const [cloneRun, setCloneRun] = useState<JobRun | null>(configSourceRun);

  // When user edits the overrides, clear any previous errors
  const [overrides, setOverrides] = useState('');
  const [overridesError, setOverridesError] = useState('');
  const [filesMap, setFilesMap] = useState<{[x: string]: any}>({});
  const onOverridesChange = useCallback((value: string) => {
    setOverrides(value);
    setOverridesError('');
  }, []);
  const [structuredOverrides, setStructuredOverrides] = useState<JsonObject>(
    overrides ? jsonParseSafe(overrides)?.run_config : {}
  );
  const [structuredFileOverrides, setStructuredFileOverrides] = useState<
    JsonObject | undefined
  >(overrides ? jsonParseSafe(overrides)?.files : {});
  const onStructuredOverridesChange = useCallback(
    (config: JsonObject, files?: JsonObject) => {
      const overridesStr = JSON.stringify(
        {
          args: [],
          entry_point: [],
          run_config: config,
          files,
        },
        null,
        4
      );
      setOverrides(overridesStr);
      setStructuredOverrides(config);
      setStructuredFileOverrides(files);
      setOverridesError('');
    },
    []
  );

  const queuesFilteredBasedOnJobs = useMemo(() => {
    return filterQueuesBasedOnJobSelection(queues, job);
  }, [job, queues]);

  const {
    runsQueueName,
    runsEntityName,
    runQueueItemID: runQueueItemToClone,
  } = determineQueueInfoFromRunConfig(cloneRun);
  const defaultQueue = useMemo(
    () =>
      getDefaultQueue(
        queuesFilteredBasedOnJobs,
        runsQueueName,
        runsEntityName,
        job
      ),
    [queuesFilteredBasedOnJobs, runsQueueName, runsEntityName, job]
  );
  const defaultVariables = defaultQueue?.variables ?? [];
  const [selectedQueue, setSelectedQueue] = useState<QueueOption | null>(
    defaultQueue
  );
  const [overridesMode, setOverridesMode] = useState(OverridesMode.BASIC);
  const variantBasic =
    overridesMode === OverridesMode.BASIC ? 'secondary' : 'ghost';
  const variantAdvanced =
    overridesMode === OverridesMode.ADVANCED ? 'secondary' : 'ghost';

  const setConfig = useCallback(
    (
      config: JsonObject,
      args?: string[],
      entrypoint?: string[],
      files?: JsonObject
    ) => {
      const newOverrides: {[x: string]: any} = {
        args,
        run_config: config,
        entry_point: entrypoint,
        files,
      };

      if (files != null && Object.keys(files).length > 0) {
        newOverrides.files = {...files};
      }
      const overrideStr = JSON.stringify(newOverrides, null, 4);
      onOverridesChange(overrideStr);
      onStructuredOverridesChange(config, files);
    },
    [onOverridesChange, onStructuredOverridesChange]
  );
  const [templateVariables, setTemplateVariables] =
    useState<TemplateVariable[]>(defaultVariables);
  const [variableValues, setVariableValues] = useState<
    Record<string, number | string>
  >(getDefaultVariableValuesFromQueueVariables(defaultVariables));

  const updateVariable = useCallback(
    (name: string, value: string | number) => {
      const templateVar = templateVariables.find(tv => tv.name === name);
      if (templateVar == null) {
        toast(
          'Resource config value not saved, please select resource config values'
        );
        return;
      }
      let updateValue = value;
      try {
        const tvSchema = JSON.parse(templateVar.schema);
        if (tvSchema.type === 'number' || tvSchema.type === 'integer') {
          updateValue = Number(value);
        }
      } catch {
        toast(
          `Invalid schema for variable: ${name} please reconfigure queue by visiting the queue tab.`
        );
        return;
      }
      setVariableValues({
        ...variableValues,
        [name]: updateValue,
      });
    },
    [templateVariables, variableValues]
  );

  const contents = useMemo(
    () =>
      getJobDefinitionFileOp({
        entityName: job.entityName,
        projectName: job.projectName,
        jobName: job.name,
        alias: job.versionInfo.runTarget ?? '',
      }),
    [job.entityName, job.projectName, job.name, job.versionInfo.runTarget]
  );
  const metadataContents = useMemo(
    () =>
      getJobMetadataOp({
        entityName: job.entityName,
        projectName: job.projectName,
        jobName: job.name,
        alias: job.versionInfo.runTarget ?? '',
      }),
    [job.entityName, job.projectName, job.name, job.versionInfo.runTarget]
  );

  const {result: jobDefinitionStr, loading} = useNodeValue(contents);
  const {result: jobMetadata, loading: metadataLoading} =
    useNodeValue(metadataContents);
  // new jobs have input and output shape on metadata instead of job def file
  const usesJobArtifactMetadata = jobMetadata?.input_types != null;
  const [showResourceConfig, setShowResourceConfig] = useState(false);
  const [resourceConfig, setResourceConfig] = useState('{}');
  const [resourceConfigError, setResourceConfigError] = useState('');
  const onResourceConfigChange = (value: string) => {
    setResourceConfig(value);
    setResourceConfigError('');
  };

  const jobDefinitionServices = useMemo(
    () => jsonParseSafe(jobDefinitionStr)?.services ?? {},
    [jobDefinitionStr]
  );

  const hasAllRequiredServices = useMemo(
    () =>
      validateRequiredServices(
        jobDefinitionServices,
        selectedQueue?.fullConfig
      ),
    [jobDefinitionServices, selectedQueue?.fullConfig]
  );

  const missingRequiredServices = useMemo(
    () =>
      getMissingRequiredServices(
        jobDefinitionServices,
        selectedQueue?.fullConfig
      ),
    [jobDefinitionServices, selectedQueue?.fullConfig]
  );

  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();
  const shouldSeeAdditionalServices = false;
  const isPublicQueue = Boolean(
    selectedQueue &&
      selectedQueue.queue === WANDB_PUBLIC_QUEUE_NAME &&
      selectedQueue.entity === WANDB_ENTITY_NAME
  );
  const shouldIncludeAdditionalServicesInConfig =
    isPublicQueue &&
    !isFeatureFlagLoading &&
    hasPublicQueueAccess &&
    ALLOWED_JOBS.includes(job.name);

  const [additionalServices, setAdditionalServices] = useState<
    AdditionalServiceData[]
  >([]);

  useEffect(() => {
    if (!selectedQueue || !shouldIncludeAdditionalServicesInConfig) {
      return;
    }

    const additionalServices = Object.keys(selectedQueue?.fullConfig ?? {})
      .filter(serviceName => serviceName !== selectedQueue?.resourceType)
      .filter(serviceName => serviceName in jobDefinitionServices)
      .filter(serviceName => jobDefinitionServices[serviceName] !== 'never')
      .map(serviceName => ({
        name: serviceName,
        config: selectedQueue?.fullConfig[serviceName] as JsonObject,
        configString: JSON.stringify(
          selectedQueue?.fullConfig[serviceName],
          null,
          2
        ),
        displayType: 'json',
        isValid: true,
      }));

    setAdditionalServices(additionalServices);
  }, [
    selectedQueue,
    shouldIncludeAdditionalServicesInConfig,
    jobDefinitionServices,
  ]);

  const updateAdditionalService = (
    serviceName: string,
    update: {
      configString?: string;
      isValid?: boolean;
      displayType?: string;
    }
  ) => {
    if (!shouldIncludeAdditionalServicesInConfig) {
      return;
    }
    setAdditionalServices(
      additionalServices.map(service =>
        service.name === serviceName ? {...service, ...update} : service
      )
    );
  };
  const [displayMode, setDisplayMode] = useState('json');
  const variantYaml = displayMode === 'yaml' ? 'secondary' : 'ghost';
  const variantJson = displayMode === 'json' ? 'secondary' : 'ghost';
  const sortedTemplateVariables = useMemo(() => {
    if (selectedQueue != null) {
      const relevantVariables = shouldIncludeAdditionalServicesInConfig
        ? getRelevantVariables(selectedQueue?.config ?? {}, templateVariables)
        : templateVariables;

      return sortTemplateVariablesByConfig(
        JSON.stringify(selectedQueue?.config ?? {}),
        relevantVariables
      );
    }
    return [];
  }, [
    selectedQueue,
    templateVariables,
    shouldIncludeAdditionalServicesInConfig,
  ]);
  const queueUsingTemplateVariables = templateVariables.length > 0;

  const setEditorType = (type: string) => {
    if (displayMode === type) {
      return;
    }
    // Convert the blob from previous format to current format
    if (type === 'json') {
      try {
        let parsed = YAML.parse(resourceConfig);
        if (!parsed) {
          parsed = {};
        }
        setResourceConfig(JSON.stringify(parsed, null, 2));
        setDisplayMode('json');
      } catch (e) {
        toast.error('Invalid YAML');
        return;
      }
    } else if (type === 'yaml') {
      try {
        let parsed = JSON.parse(resourceConfig);
        if (resourceConfig === '{}') {
          parsed = undefined; // show nothing for yaml when empty
        }
        setResourceConfig(YAML.stringify(parsed, {indent: 2}));
        setDisplayMode('yaml');
      } catch (e) {
        toast.error('Invalid JSON');
        return;
      }
    }
  };

  const [upsertProject] = useUpsertModelMutation();
  const [createDefaultResourceConfig] =
    useCreateDefaultResourceConfigMutation();
  const [createRunQueue] = useCreateRunQueueMutation();
  const [pushToRunQueue] = usePushToRunQueueMutation({
    context: propagateErrorsContext(),
  });
  const [priority, setPriority] = useState(PRIORITY_LEVEL.MEDIUM);
  const priorityMetadata = useMemo(
    () => PRIORITY_LEVEL_METADATA[priority],
    [priority]
  );

  // TODO(np): Actually hook this up once queue prioritization mode info
  // is available from the backend.  For now, just say that only docker queues
  // support prioritization.
  const queueSupportsPrioritization = useMemo(
    () => selectedQueue != null && isQueuePrioritizationEnabled(selectedQueue),
    [selectedQueue]
  );

  // If the selected queue doesn't support prioritization, reset the priority
  // Avoid potentially confusing visual state where the disabled priority selector
  // shows a priority that would actually be ignored by backend.
  const effectivePriority = useMemo(
    () => (queueSupportsPrioritization ? priority : PRIORITY_LEVEL.MEDIUM),
    [priority, queueSupportsPrioritization]
  );

  const target = usePageProjectAndEntityTarget();

  const [targetEntityName, setTargetEntityName] = useState(
    target.targetEntityName
  );
  const [targetProjectName, setTargetProjectName] = useState(
    target.targetProjectName
  );

  useEffect(() => {
    onTargetEntityChange?.(targetEntityName);
  }, [targetEntityName, onTargetEntityChange]);

  const onSelectProject = (option: ProjectOption) => {
    setTargetEntityName(option.entityName);
    setTargetProjectName(option.projectName);
    onTargetEntityChange?.(option.entityName);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const onPopoverClose = () => {
    setAnchorEl(null);
  };

  const substituteVariables = (
    config: JsonObject,
    variables: Record<string, number | string>
  ): JsonObject => {
    function injectVarsIntoString(str: string): string | number {
      let singleValue: string | number | null = null;
      const replaceString = str.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        singleValue = variables[varName];
        return variables[varName]?.toString() ?? match;
      });
      if (isSingleVarString(str) && singleValue != null) {
        return singleValue;
      }
      return replaceString;
    }

    function substitute(obj: any): any {
      if (typeof obj === 'string') {
        return injectVarsIntoString(obj);
      } else if (typeof obj === 'number') {
        return obj;
      } else if (Array.isArray(obj)) {
        return obj.map(item => substitute(item));
      } else if (typeof obj === 'object' && obj !== null) {
        return Object.fromEntries(
          Object.entries(obj).map(([key, val]) => [key, substitute(val)])
        );
      }
      return obj;
    }

    return substitute(config);
  };

  const onSelectQueue = useCallback((option: QueueOption) => {
    setSelectedQueue(option);
    setTemplateVariables(option.variables);
    setVariableValues(
      getDefaultVariableValuesFromQueueVariables(option.variables)
    );
  }, []);

  useEffect(() => {
    // reset clone run if the current cloned run not in list of runs for version
    // and is not null (meaning the user didn't edit it)

    // don't reset if cloneRun matches configSourceRun because this means the
    // user explicitly imported the run and we don't want to override it based
    // on previous runs which only contain the user's runs. they could be
    // importing a teammate's run or an older run not in list of prevRuns.
    if (cloneRun?.name === configSourceRun?.name) {
      return;
    }

    const newCloneRun = getDefaultCloneFrom(
      previousRuns,
      viewer?.username ?? '',
      cloneRun?.name
    );
    if (
      newCloneRun != null &&
      cloneRun != null &&
      newCloneRun.name !== cloneRun?.name
    ) {
      setCloneRun(newCloneRun);
    }
    if (
      !jobDefinitionStr ||
      Boolean(overrides) ||
      Object.keys(structuredOverrides).length > 0
    ) {
      // We need the job definition string for both defaults and ordering.
      return;
    }

    const jobfile = new JobFile(jobDefinitionStr);
    setConfig(jobfile.getConfigInputExample(), [], []);
  }, [
    cloneRun,
    configSourceRun,
    previousRuns,
    viewer?.username,
    jobDefinitionStr,
    overrides,
    structuredOverrides,
    setConfig,
  ]);

  const queryRunQueueItem = useRunQueueItem(
    entityName,
    DEFAULT_LAUNCH_PROJECT_NAME,
    defaultQueue?.queue ?? '',
    runQueueItemToClone ?? ''
  );

  const queryRunFiles = useRunFilesAndFolderWithDirectUrLsQuery({
    variables: {
      projectName,
      entityName,
      runName: cloneRun?.name ?? '',
      maxNumFiles: 10,
      pattern: '_wandb_configs/_%',
    },
  });

  useEffect(() => {
    const loadFilesMap = async () => {
      const updatedFilesMap = await populateJobFilesMap(queryRunFiles);
      setFilesMap(updatedFilesMap);
    };
    loadFilesMap();
  }, [queryRunFiles]);

  // update overrides block when job or cloned run changes
  // could happen when user selects new job
  // or when user selects a run to clone outside of the
  // drawer eg. from the runs table
  useEffect(() => {
    if (!cloneRun) {
      return;
    }
    const rqiOverrides = queryRunQueueItem.runQueueItem?.runSpec?.overrides;
    if (!jobDefinitionStr) {
      return;
    }
    const jobfile = new JobFile(jobDefinitionStr, jobMetadata);
    const orderedConfig = jobfile.getOrderedClonedRunConfig(cloneRun.config);
    const prunedFilesMap = jobfile.getPrunedFilesMap(filesMap);
    if (
      runQueueItemToClone &&
      rqiOverrides &&
      !queryRunQueueItem.loading &&
      !queryRunQueueItem.error
    ) {
      const runConfig = rqiOverrides.run_config ?? orderedConfig;
      const filesValue = rqiOverrides.files ?? prunedFilesMap;
      setConfig(
        runConfig,
        rqiOverrides.args,
        rqiOverrides.entry_point,
        filesValue
      );
    } else if (jobDefinitionStr) {
      setConfig(orderedConfig, cloneRun.args, [], prunedFilesMap);
    }
  }, [
    queryRunQueueItem,
    cloneRun,
    jobDefinitionStr,
    runQueueItemToClone,
    jobMetadata,
    filesMap,
    usesJobArtifactMetadata,
    setConfig,
    viewer,
  ]);

  // when run queue item query changes because of cloneRun change
  // update the template variable values
  useEffect(() => {
    // update variable values
    if (
      queryRunQueueItem.loading ||
      selectedQueue?.queueID !== queryRunQueueItem.runQueueItem?.queueId ||
      selectedQueue == null
    ) {
      return;
    }

    // update variables
    if (selectedQueue?.variables) {
      setTemplateVariables(selectedQueue.variables);
    }

    if (queryRunQueueItem.runQueueItem?.templateVariableValues) {
      const runVarValues = pruneTemplateVariableValues(
        queryRunQueueItem.runQueueItem.templateVariableValues,
        selectedQueue?.variables ?? []
      );
      const queueDefaults = getDefaultVariableValuesFromQueueVariables(
        selectedQueue.variables
      );
      const mergedValues = {...queueDefaults, ...runVarValues};

      setVariableValues(mergedValues);
      return;
    }
    if (selectedQueue?.variables) {
      setVariableValues(
        getDefaultVariableValuesFromQueueVariables(selectedQueue.variables)
      );
    }
  }, [
    queryRunQueueItem.loading,
    queryRunQueueItem.runQueueItem?.queueId,
    queryRunQueueItem.runQueueItem?.templateVariableValues,
    selectedQueue,
  ]);

  // Set the resource config when the queue or variables change.
  useEffect(() => {
    if (!selectedQueue) {
      return;
    }
    const substitutedConfig = substituteVariables(
      selectedQueue.config,
      variableValues
    );
    setResourceConfig(
      displayMode === 'yaml'
        ? YAML.stringify(substitutedConfig, {indent: 2})
        : JSON.stringify(substitutedConfig, null, 2)
    );
  }, [selectedQueue, displayMode, variableValues]);

  const {openPopover} = useRunQueuePopover();

  if (loading || metadataLoading) {
    return <Loader active name="job-drawer-inner-loader" />;
  }

  const validate = (
    overridesToValidate: string,
    resourceConfigToValidate: string
  ): boolean => {
    let ok = true;
    try {
      JSON.parse(overridesToValidate);
    } catch (err) {
      setOverridesError('Overrides must be valid ');
      ok = false;
    }
    const rc = resourceConfigToValidate.trim() || '{}';
    try {
      if (displayMode === 'yaml') {
        YAML.parse(rc);
      } else {
        JSON.parse(rc);
      }
    } catch (err) {
      setResourceConfigError(
        'Resource config must be valid ' + displayMode.toUpperCase()
      );
      ok = false;
    }
    return ok;
  };

  const onLaunch = () => {
    let overridesToSend = overrides;
    if (overridesMode === OverridesMode.BASIC && jobMetadata.input_schemas) {
      overridesToSend = JSON.stringify(
        {
          args: [],
          entry_point: [],
          run_config: structuredOverrides,
          files: structuredFileOverrides,
        },
        null,
        4
      );
    }
    if (onFinalize != null) {
      if (!validate(overridesToSend, resourceConfig)) {
        return;
      }
      const rc = JSON.parse(resourceConfig);
      const resource = selectedQueue!.resourceType ?? 'local-container';
      let launchSpec = {
        entity: targetEntityName,
        project: targetProjectName,
        queue: selectedQueue!.queue,
        queueEntity: selectedQueue!.entity,
        job: `${entityName}/${projectName}/${job.name}:${job.versionInfo.runTarget}`,
        overrides: JSON.parse(overridesToSend),
        priority: effectivePriority,
        template_variables: variableValues,
      } as LaunchSpec;
      // if not empty, add resource_args
      if (Object.keys(rc).length > 0) {
        launchSpec = {...launchSpec, resource_args: {[resource]: rc}};
      }
      onFinalize(launchSpec);
      return;
    }

    if (
      shouldIncludeAdditionalServicesInConfig &&
      additionalServices.length > 0
    ) {
      const validateServiceConfig = (
        service: AdditionalServiceData
      ): boolean => {
        try {
          if (service.displayType === 'yaml') {
            YAML.parse(service.configString);
          } else {
            JSON.parse(service.configString);
          }
          return true;
        } catch (err) {
          return false;
        }
      };

      if (!additionalServices.some(validateServiceConfig)) {
        toast.error('Invalid additional services configuration');
        return;
      }
    }

    // actually pushing job to queue
    setIsSubmitting(true);
    if (validate(overridesToSend, resourceConfig)) {
      const additionalServicesToSend =
        additionalServices?.map(service => ({
          name: service.name,
          config:
            service.displayType === 'yaml'
              ? YAML.parse(service.configString)
              : JSON.parse(service.configString),
        })) ?? [];

      pushRunToQueue(overridesToSend, additionalServicesToSend);
      Analytics.track('clicked launch now', {
        entityName,
        projectName,
        resourceConfig,
        location: 'job drawer',
      });
    } else {
      setIsSubmitting(false);
    }
  };

  const pushRunToQueue = async (
    overridesToSend: string,
    additionalServices?: {
      name: string;
      config: string;
    }[]
  ) => {
    if (!selectedQueue) {
      setIsSubmitting(false);
      return;
    }
    let queueName = selectedQueue.queue;
    // creationEntity is the entity a new queue would be created in.
    const creationEntity =
      entityName === WANDB_ENTITY_NAME ? viewerEntity : entityName;
    const queueEntity = selectedQueue.entity || creationEntity;
    let queueID = selectedQueue.queueID;
    if (selectedQueue.queue === '') {
      // Create a starter queue
      // First, ensure we have the necessary project for an entity-scoped queue.
      const createProjectResponse = await upsertProject({
        variables: {
          entityName: creationEntity,
          name: DEFAULT_LAUNCH_PROJECT_NAME,
        },
      });
      if (createProjectResponse.errors != null) {
        toast('Error creating entity queue project.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'upsertProject',
          errors: createProjectResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }

      const completeConfig = {
        resource_args: {
          'local-container': {},
        },
      };
      const createConfigResponse = await createDefaultResourceConfig({
        variables: {
          entityName: creationEntity,
          resource: 'local-container',
          config: JSON.stringify(completeConfig),
          templateVariables: '{}',
        },
      });
      if (createConfigResponse.errors != null) {
        toast('Error creating entity queue config.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'createDefaultResourceConfig',
          errors: createConfigResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }
      const defaultResourceConfigID =
        createConfigResponse.data?.createDefaultResourceConfig
          ?.defaultResourceConfigID;

      queueName = 'Starter queue';
      const createQueueResponse = await createRunQueue({
        variables: {
          entityName: creationEntity,
          projectName: DEFAULT_LAUNCH_PROJECT_NAME,
          queueName,
          access: RunQueueAccessType.Project,
          defaultResourceConfigID,
          prioritizationMode: RunQueuePrioritizationMode.Disabled,
        },
      });
      if (createQueueResponse.errors != null) {
        toast('Error creating entity queue.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'createRunQueue',
          errors: createConfigResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }
      Analytics.track('create run queue success', {
        entityName: creationEntity,
        queueName,
        location: 'job drawer',
      });
      queueID = createQueueResponse.data?.createRunQueue?.queueID ?? '';
    }

    const config: JsonObject = {
      entity: targetEntityName,
      project: targetProjectName,
      job: `${entityName}/${projectName}/${job.name}:${job.versionInfo.runTarget}`,
      overrides: JSON.parse(overridesToSend),
    };

    if (shouldIncludeAdditionalServicesInConfig && additionalServices) {
      config.additional_services = additionalServices;
    }

    if (selectedQueue.resourceType) {
      config.resource_args = {
        [selectedQueue.resourceType]:
          displayMode === 'json'
            ? JSON.parse(resourceConfig)
            : YAML.parse(resourceConfig),
      };
    }

    const pushingConfig = config;
    if (queueUsingTemplateVariables) {
      delete pushingConfig.resource_args;
    }
    const pushedRunSpec = JSON.stringify(pushingConfig);
    try {
      const pushResp = await pushToRunQueue({
        variables: {
          queueID,
          runSpec: pushedRunSpec,
          priority: effectivePriority,
          templateVariableValues: templateVariables
            ? JSON.stringify(variableValues)
            : null,
        },
      });
      const newItemId = pushResp.data?.pushToRunQueue?.runQueueItemId;
      const usesPublicQueue =
        selectedQueue.entity === WANDB_ENTITY_NAME &&
        selectedQueue.queue === WANDB_PUBLIC_QUEUE_NAME;
      if (newItemId) {
        !usesPublicQueue &&
          toast.success(
            <S.SuccessfulQueue>
              <IconInfo width={48} height={48} />
              <S.SuccessfulQueueMessage>
                Your job was successfully added to '{queueName}'.{' '}
                <TargetBlank
                  data-test="view-queue-link"
                  href={queueUrl(queueEntity, queueID, 'runs')}>
                  View queue
                </TargetBlank>
              </S.SuccessfulQueueMessage>
            </S.SuccessfulQueue>
          );
        setIsSubmitting(false);
        // open popover after successful job launch. delay to allow drawer to close first
        setTimeout(() => {
          try {
            const settingFromLocalStorage = safeLocalStorage.getItem(
              'openPopoverAfterEachLaunch'
            );
            const shouldOpenPopover = settingFromLocalStorage
              ? settingFromLocalStorage === 'true'
              : true;
            if (shouldOpenPopover) {
              openPopover();
            }
          } catch (e) {
            // if we can't access localStorage, default to opening the popover
            openPopover();
          }
        }, 300);
      } else {
        throw new Error();
      }
    } catch (err) {
      const errorMsg: string =
        (err as any)?.networkError?.result?.errors?.[0]?.message ??
        'Something went wrong while trying to queue this job.';
      toast.error(errorMsg);
      setIsSubmitting(false);
      return;
    }
    onClose();
  };

  // Don't show resource configuration if creating a starter queue
  const hasResourceConfig =
    selectedQueue && selectedQueue.resourceType && selectedQueue.queue !== '';

  const missingRequiredServicesMessage =
    'Required services missing from queue: ' +
    missingRequiredServices.join(', ') +
    '. Please select a different queue' +
    (isPublicQueue ? '.' : ' or modify the queue configuration.');

  const publicJobSelected =
    job && isAllowedWandbPublicJob(job.name, job.projectName, job.entityName);

  const shouldHidePriorityForPublicJobs = publicJobSelected;

  return (
    <>
      {queryRunQueueItem.loading && (
        <Loader active name="job-drawer-inner-loader" />
      )}
      <Tailwind>
        <S.JobDrawerInner>
          <S.JobDrawerInnerMain>
            <div className="mb-4 mt-16 gap-[4px] pb-[4px]">
              <div className="text-[1rem] font-semibold leading-[2rem]">
                Destination project
              </div>
              <div className="text-sm text-moon-650 mt-1">
                Choose where the resulting run will be produced.
              </div>
            </div>
            <div className="m-0 flex flex-col text-sm px-[1px]">
              <FilterableSelectProject
                defaultEntityName={targetEntityName}
                defaultProjectName={targetProjectName}
                projectName={projectName}
                queueEntityName={selectedQueue?.entity ?? ''}
                queueName={selectedQueue?.queue ?? ''}
                onSelectProject={onSelectProject}
              />
            </div>
            <div className="mb-4 mt-16 flex items-center">
              <div className="text-[1rem] font-semibold leading-[2rem]">
                Job overrides
              </div>
              <div style={{flex: 1}}></div>
              {jobMetadata.input_schemas && (
                <div className="flex flex-none justify-start gap-[8px]">
                  <div>
                    <Button
                      variant="secondary"
                      size="small"
                      data-test="job-drawer-config-importer-button"
                      icon="import-insert"
                      disabled={lockedRunToClone}
                      onClick={openConfigImporter}>
                      Import Configuration
                    </Button>
                  </div>
                  <div className="flex flex-none justify-start">
                    <Button
                      variant={variantBasic}
                      size="small"
                      onClick={() => setOverridesMode(OverridesMode.BASIC)}>
                      Basic
                    </Button>
                    <Button
                      variant={variantAdvanced}
                      size="small"
                      onClick={() => setOverridesMode(OverridesMode.ADVANCED)}
                      data-test="overrides-advanced">
                      Advanced
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {overridesMode === OverridesMode.BASIC &&
              jobMetadata.input_schemas && (
                <JobDrawerStructuredOverrides
                  overrides={structuredOverrides}
                  fileOverrides={structuredFileOverrides}
                  onStructuredOverridesChange={onStructuredOverridesChange}
                  onSelectRun={setCloneRun}
                  schemas={jobMetadata.input_schemas}
                  targetEntityName={targetEntityName}
                  openSecretsView={openSecretsView}
                />
              )}
            {(overridesMode === OverridesMode.ADVANCED ||
              !jobMetadata.input_schemas) && (
              <JobDrawerOverrides
                overrides={overrides}
                onOverridesChange={onOverridesChange}
                onSelectRun={setCloneRun}
              />
            )}
            {overrideHintText && <S.HintText>{overrideHintText}</S.HintText>}
            {overridesError && <S.Error>{overridesError}</S.Error>}
            <div className="mb-4 mt-16 flex items-center">
              <div className="text-[1rem] font-semibold leading-[2rem]">
                Queue
              </div>
            </div>
            <div className="m-0 flex flex-col text-sm px-[1px]">
              <SelectQueue
                options={queuesFilteredBasedOnJobs}
                selectedOption={selectedQueue}
                onSelectQueue={onSelectQueue}
              />
            </div>
            <S.HintText>
              {isPublicQueue && publicJobSelected
                ? `Public jobs can only be launched on the ${WANDB_PUBLIC_QUEUE_NAME} public queue.`
                : 'Inactive queues are unavailable for selection.'}
            </S.HintText>
            {shouldHidePriorityForPublicJobs ? null : (
              <>
                <div className="mb-4 mt-16 flex items-center">
                  <div className="text-[1rem] font-semibold leading-[2rem]">
                    Job Priority
                  </div>
                </div>
                <SelectPriority
                  value={effectivePriority}
                  disabled={!queueSupportsPrioritization}
                  onSelectPriority={setPriority}
                />
                {!queueSupportsPrioritization ? (
                  <S.HintText>
                    The selected queue does not support job prioritization.
                  </S.HintText>
                ) : (
                  <S.PriorityHints>
                    <S.PriorityHint>
                      <Icon name="checkmark-circle" width={18} height={20} />
                      {priorityMetadata.placementText}
                    </S.PriorityHint>
                    {/* TODO(np): Hide this until we actually support preemption

<S.PriorityHint>
<Icon name="checkmark-circle" width={18} height={20} />
{priorityMetadata.adminApprovalText}
</S.PriorityHint>

<S.PriorityHint>
<Icon name="checkmark-circle" width={18} height={20} />
{priorityMetadata.preemptionText}
</S.PriorityHint>
*/}
                  </S.PriorityHints>
                )}
              </>
            )}
            {hasResourceConfig && !isPublicQueue && (
              <>
                {sortedTemplateVariables.length > 0 && (
                  <div className="mb-4 mt-16 flex items-center">
                    <div className="text-[1rem] font-semibold leading-[2rem]">
                      Queue Configurations
                    </div>
                  </div>
                )}
                {sortedTemplateVariables.length > 0 &&
                  !queryRunQueueItem.loading &&
                  sortedTemplateVariables.map((variable, i) => (
                    <ResourceVariableForm
                      key={i}
                      name={variable.name}
                      schema={variable.schema}
                      description={variable.description}
                      value={variableValues[variable.name]}
                      updateVariable={updateVariable}
                    />
                  ))}
                <div className="mb-4 mt-16 flex items-center">
                  <div>Complete queue configurations</div>
                  <S.ToggleConfig>
                    {showResourceConfig && (
                      <Button
                        variant={variantYaml}
                        onClick={() => setEditorType('yaml')}>
                        YAML
                      </Button>
                    )}
                    {showResourceConfig && (
                      <Button
                        variant={variantJson}
                        onClick={() => setEditorType('json')}>
                        JSON
                      </Button>
                    )}
                    <Button
                      data-test="show-resource-config"
                      variant="ghost"
                      onClick={() => {
                        setShowResourceConfig(!showResourceConfig);
                      }}>
                      {showResourceConfig ? 'Hide' : 'Show'}
                    </Button>
                  </S.ToggleConfig>
                </div>
                {showResourceConfig && (
                  <CodeEditor
                    value={resourceConfig}
                    language={displayMode}
                    onChange={onResourceConfigChange}
                    readOnly={queueUsingTemplateVariables || isPublicQueue}
                  />
                )}
                {resourceConfigError && (
                  <S.Error>{resourceConfigError}</S.Error>
                )}
              </>
            )}
            {shouldSeeAdditionalServices &&
              additionalServices.length > 0 &&
              !queryRunQueueItem.loading &&
              additionalServices.map((service: AdditionalServiceData) => {
                return (
                  <JobDrawerAdditionalServiceConfigEditor
                    key={service.name}
                    additionalServiceName={service.name}
                    serviceConfig={service.config}
                    templateVariables={getRelevantVariables(
                      service.config,
                      templateVariables
                    )}
                    config={service.configString}
                    updateAdditionalService={update =>
                      updateAdditionalService(service.name, update)
                    }
                    displayMode={service.displayType ?? 'json'}
                    variableValues={variableValues}
                    updateVariable={updateVariable}
                  />
                );
              })}

            {selectedQueue == null && (
              <S.HintText>Select a queue to see available projects.</S.HintText>
            )}
            {selectedQueue &&
              !hasAllRequiredServices &&
              missingRequiredServices.length > 0 && (
                <S.HintText>{missingRequiredServicesMessage}</S.HintText>
              )}
            {children}
          </S.JobDrawerInnerMain>
        </S.JobDrawerInner>
        <S.StickyFooter>
          <div className="mx-[-8px] relative pointer-events-none border-b border-moon-250" />
          <div className="py-[20px] px-[24px]">
            <Button
              data-test="launch-button"
              className="w-full"
              variant="primary"
              size="large"
              onClick={onLaunch}
              disabled={
                !selectedQueue ||
                !targetEntityName ||
                !targetProjectName ||
                isSubmitting ||
                queryRunQueueItem.loading ||
                !hasAllRequiredServices
              }>
              {finalizeCTA ?? 'Launch'}
            </Button>
          </div>
        </S.StickyFooter>
      </Tailwind>
    </>
  );
};

const FilterableSelectProject = ({
  defaultEntityName,
  defaultProjectName,
  projectName,
  queueEntityName,
  queueName,
  onSelectProject,
}: {
  defaultEntityName: string;
  defaultProjectName: string;
  projectName: string;
  queueEntityName: string;
  queueName: string;
  onSelectProject: (option: ProjectOption) => void;
}) => {
  const viewer = useViewer();

  const [projectSearchString, setProjectSearchString] = useState('');

  const {options, loading} = useDestinationProjectOptions(
    projectName,
    queueEntityName,
    queueName,
    viewer?.entity ?? '',
    projectSearchString
  );

  // In order to show the current project in the dropdown, we need to consider
  // that it might not be in the list of options because the backend limits the
  // number of projects that can be fetched.
  const optionsWithDefault = useMemo(() => {
    // use the default project if it already exists in options
    let defaultProjectExists = false;
    for (const optionGroup of options) {
      for (const option of optionGroup.options) {
        if (
          option.entityName === defaultEntityName &&
          option.projectName === defaultProjectName
        ) {
          defaultProjectExists = true;
          break;
        }
      }
      if (defaultProjectExists) {
        break;
      }
    }

    // we have to manually add if it doesn't exist in the list of options
    if (!defaultProjectExists && defaultEntityName && defaultProjectName) {
      const defaultOption: ProjectOption = {
        value: `${defaultEntityName}/${defaultProjectName}`,
        entityName: defaultEntityName,
        projectId: '', // Not used at all in SelectProject
        projectName: defaultProjectName,
      };

      // Find the entity group or create one if it doesn't exist
      const entityGroupIndex = options.findIndex(
        group => group.label === defaultEntityName
      );

      if (entityGroupIndex >= 0) {
        return options.map((group, index) =>
          index === entityGroupIndex
            ? {
                ...group,
                options: [defaultOption, ...group.options],
              }
            : group
        );
      } else {
        const newGroup = {
          label: defaultEntityName,
          options: [defaultOption],
        };
        return [newGroup, ...options];
      }
    }

    return options;
  }, [options, defaultEntityName, defaultProjectName]);

  // show selector only after the first options load to ensure
  // the default project is available. Subsequent fetches (triggered by
  // user typing) will no longer unmount the component, preserving the input
  // value and keeping the dropdown open.
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  useEffect(() => {
    if (!loading) {
      setInitialLoadComplete(true);
    }
  }, [loading]);

  const handleMenuClose = useCallback(() => {
    setProjectSearchString('');
  }, []);

  if (!initialLoadComplete) {
    return null;
  }

  return (
    <SelectProject
      defaultEntityName={defaultEntityName}
      defaultProjectName={defaultProjectName}
      options={optionsWithDefault}
      onSelectProject={onSelectProject}
      setProjectSearchString={setProjectSearchString}
      onMenuClose={handleMenuClose}
    />
  );
};
